import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function useCategoryMutations() {
  const app = useRealmApp();
  return {
    addCategory: useAddCategory(app.currentUser.id),
    updateCategory: useUpdateCategory(app.currentUser.id),
    deleteCategory: useDeleteCategory(app.currentUser.id),
  };
}

const AddCategoryMutation = gql`
  mutation AddCategory($category: CategoryInsertInput!) {
    addedCategory: insertOneCategory(data: $category) {
      _id
      name
      color
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateCategoryMutation = gql`
  mutation UpdateCategory(
    $categoryId: ObjectId!
    $ownerId: String!
    $updates: CategoryUpdateInput!
  ) {
    updatedCategory: updateOneCategory(
      query: { _id: $categoryId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      name
      color
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteCategoryMutation = gql`
  mutation DeleteCategory($categoryId: ObjectId!, $ownerId: String!) {
    deletedCategory: deleteOneCategory(
      query: { _id: $categoryId, owner_id: $ownerId }
    ) {
      _id
    }
  }
`;

const CategoryFieldsFragment = gql`
  fragment CategoryFields on Category {
    _id
    name
    color
    updateDatetime
    creationDatetime
  }
`;

function useAddCategory(ownerId) {
  const [addCategoryMutation] = useMutation(AddCategoryMutation, {
    // Manually save added Categories into the Apollo cache so that Category queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedCategory } }) => {
      cache.modify({
        fields: {
          categories: (existingCategories = []) => [
            ...existingCategories,
            cache.writeFragment({
              data: addedCategory,
              fragment: CategoryFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (category) => {
    const {
      data: { addedCategory },
    } = await addCategoryMutation({
      variables: {
        category: {
          owner_id: ownerId,
          ...category,
        },
      },
    });
    return addedCategory;
  };
}

function useUpdateCategory(ownerId) {
  const [updateCategoryMutation] = useMutation(UpdateCategoryMutation);
  //
  return async (category, updates) => {
    const {
      data: { updatedCategory },
    } = await updateCategoryMutation({
      variables: { categoryId: category._id, ownerId, updates },
    });
    return updatedCategory;
  };
}

function useDeleteCategory(ownerId) {
  const [deleteCategoryMutation] = useMutation(DeleteCategoryMutation, {
    update: (cache, { data: { deletedCategory } }) => {
      cache.modify({
        fields: {
          categories: (existingCategories = [], { readField }) =>
            existingCategories.filter(
              (ref) => deletedCategory._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (category) => {
    const {
      data: { deletedCategory },
    } = await deleteCategoryMutation({
      variables: { categoryId: category._id, ownerId },
    });
    return deletedCategory;
  };
}
